<template>
    <div class="conditionally-approve-wizard suspend-wizard">
        <loan-notes-component v-if="wizardStep === 0" 
            :loan-id="loanId" 
            :wizard-view="true"
            :default-note="'Suspend'"
            v-on:wizardnoteadded="addWizardNote"></loan-notes-component>

        <div class="processor-select__container" v-if="wizardStep === 1">
            <p class="processor--info">
                <span class="k-icon k-i-information k-i-info"></span> 
                Listed below is the current processor on the file. You have the ability to reassign to a different processor using the dropdown below as necessary
            </p>
            <h3 class="step--label">Current Assigned Processor</h3>
            <k-dropdown :data-items="filteredProcessors"
                :text-field="'displayName'"
                :data-item-key="'id'"
                :value="selectedProcessor"
                :filterable="true"
                @filterchange="onProcessorFilterChange"
                @change="onProcessorSelected"></k-dropdown>
            <k-fade :appear="selectedProcessor !== null" v-bind:class="['processor-info__container', {'container--hide': !selectedProcessor }]" >
                <div v-if="selectedProcessor">
                    <h3 class="step--label"><span class="k-icon k-i-user"></span>  {{ selectedProcessor.fullName }}</h3>
                    <p class="processor__link">Email: <a :href="'mailto: ' + selectedProcessor.email">{{ selectedProcessor.email }}</a></p>
                    <p v-bind:class="['processor__link', { 'processor__link--disabled': !selectedProcessor.cellPhone }]">Mobile: <a :href="selectedProcessor.cellPhone ? 'tel: ' + selectedProcessor.cellPhone : ''">{{ selectedProcessor.cellPhone ? $options.filters.phoneFilter(selectedProcessor.cellPhone) : 'N/A' }}</a></p>
                    <p v-bind:class="['processor__link', { 'processor__link--disabled': !selectedProcessor.phone }]">Phone: <a :href="selectedProcessor.phone ? 'tel: ' + selectedProcessor.phone : ''">{{ selectedProcessor.phone ? $options.filters.phoneFilter(selectedProcessor.phone) : 'N/A' }}</a></p>
                </div>
            </k-fade>
        </div>   

        <div class="review-save__container" v-if="wizardStep === 2">
            <h3 class="step--label">New Notes Added</h3>
            <div class="notes__container">
                <div v-for="note in addedNotes" :key="note" class="note">
                    <blockquote v-html="note"></blockquote>
                </div>
                <div v-if="addedNotes.length === 0">
                    <p class="no-notes">No disposition notes available</p>
                </div>
            </div>
            <h3 class="step--label">Processor</h3>
            <div class="save__container">
                <k-input :value="selectedProcessor?.fullName" disabled></k-input>
                <!-- <k-button class="disposition__btn" v-on:click="dispositionLoan" :disabled="!selectedProcessor">Save</k-button> -->
            </div>
        </div>

        <div class="conditions__loader-container" v-if="isLoading">
            <loader :size="'large'" :type="'infinite-spinner'"></loader>
        </div>


        <transition name="dialogfade">
          <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
            <p class="error-dialog__msg">{{ errorMsg }}</p>
          </k-dialog>
        </transition>   
    </div>
</template>

<script>
    import auth from '../authConfig.js';
    import apiService from '../api/api.service.js';

    import cloneDeep from 'lodash.clonedeep';

    import { Dialog, DialogActionsBar, Window } from '@progress/kendo-vue-dialogs';
    import { MultiSelect, DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Button } from '@progress/kendo-vue-buttons';
    import { Input } from '@progress/kendo-vue-inputs';
    import { Fade, Reveal, Slide } from "@progress/kendo-vue-animation";

    import { filterBy } from '@progress/kendo-data-query';

    import loanNotesComponent from './loanNotesComponent.vue';

    export default {
        props: ['finishClicked', 'loanId', 'wizardStep'],
        emits: ['loansuspended', 'processorselected', 'requestfailed'],
        data() {
            return {
                addedNotes: [],
                approvalMilestone: {},
                condlApprovalMilestone: {},
                errorMsg: '',
                errorTitle: '',
                filteredProcessors: [],
                isLoading: false,
                milestones: [],
                processors: [],
                resubmittalMilestone: {},
                roleId: '',
                selectedProcessor: null,
                showErrorDialog: false,
            }
        },
        watch: {
            wizardStep(val) {
                if (val === 1) {
                    this.getEncompassUsers();
                }
            },
            finishClicked(val) {
                if (val) {
                    this.dispositionLoan();
                }
            }
        },
        mounted() {
            this.getMilestones();
        },
        methods: {
            addWizardNote(noteHtml) {
                this.addedNotes.push(noteHtml);
            },
            assignProcessors(allProcessors) {
                if(allProcessors.length > 0) {
                    this.processors = allProcessors.filter(proc => proc.userIndicators.some(indicator => indicator === 'Enabled')).sort((a, b) => a.fullName.localeCompare(b.fullName));
                    this.processors.forEach(proc => proc.displayName = `${proc.fullName}  (${proc.id})`);

                    this.filteredProcessors = cloneDeep(this.processors);

                    let currentProcessor = sessionStorage.getItem('currentprocessor');
                    this.selectedProcessor = this.filteredProcessors.find(proc => proc.id === currentProcessor);
                    this.$emit('processorselected', true);
                }
            },
            async dispositionLoan() {
                let unfinishMilestones = [];

                //check if condl approval is not current milestone, need to unfinish any subsequent milestones first, in order of newest finished, if necessary
                if (this.approvalMilestone.doneIndicator) {
                    unfinishMilestones.push(this.approvalMilestone.id);
                }

                if (this.resubmittalMilestone.doneIndicator) {
                    unfinishMilestones.push(this.resubmittalMilestone.id);
                }

                let request = {
                    loanId: this.loanId,
                    resubmittalAssociate: {
                        loanAssociateType: 'User',
                        userId: this.selectedProcessor.id,
                        loanId: this.loanId,
                        milestoneId: this.resubmittalMilestone.id
                    },
                    condlApprovalId: this.condlApprovalMilestone.id,
                    action: this.condlApprovalMilestone.doneIndicator ? "unfinish" : "finish",
                    unfinishMilestones: unfinishMilestones
                };

                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                apiService.SuspendLoan(request, tokenResponse.accessToken)
                    .then(res => {
                        sessionStorage.setItem('issuspended', "true");

                        this.isLoading = false;
                        this.$emit('loansuspended', 'File suspended successfully');
                    }).catch(error => {
                        this.errorTitle = 'Failed to Disposition Loan';
                        
                        if(error.response?.data?.data) {
                            this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                        this.$emit('requestfailed');
                    });
            },
            async getEncompassUsers() {
                if (this.processors.length > 0) return this.filteredProcessors = cloneDeep(this.processors);

                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                apiService.GetProcessors(tokenResponse.accessToken)
                    .then(res => {
                        this.assignProcessors(res.data.data);

                        this.isLoading = false;
                    }).catch(error => {
                        this.errorTitle = 'Failed to Fetch Processors';
                        
                        if(error.response?.data?.data) {
                            this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            async getMilestones(update = false) {
                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                apiService.GetMilestones(this.loanId, tokenResponse.accessToken)
                    .then(res => {
                        if (res.data.data) {
                            this.milestones = res.data.data;

                            this.condlApprovalMilestone = res.data.data.find(milestone => milestone.milestoneName === "Cond'l Approval");
                            this.resubmittalMilestone = res.data.data.find(milestone => milestone.milestoneName === "Resubmittal");
                            this.approvalMilestone = res.data.data.find(milestone => milestone.milestoneName === "Approval");
                        }
                    }).catch(error => {
                        this.errorTitle = 'Failed to Fetch Milestones';
                        
                        if(error.response?.data?.data) {
                            this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                        this.$emit('requestfailed');
                    });
            },
            onProcessorFilterChange(evt) {
                this.filteredProcessors = filterBy(this.processors, evt.filter);
            },
            onProcessorSelected(evt) {
                this.selectedProcessor = evt.value;
                this.$emit('processorselected', true);
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.toggleErrorDialog();
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if (!this.showErrorDialog) {
                    this.errorMsg = '';
                    this.errorTitle = '';
                }
            }
        },
        filters: {
            phoneFilter(val) {
                if (!val) return '';

                let formattedPhone = val.split(' ').join('').split('-').join('').split('(').join('').split(')').join('');

                if (formattedPhone.length !== 10 && formattedPhone.length !== 11) return '';

                if (formattedPhone.length === 10) {
                    return `(${formattedPhone.substring(0, 3)}) ${formattedPhone.substring(3, 6)}-${formattedPhone.substring(6)}`;
                } else {
                    return `${formattedPhone.substring(0, 1)} (${formattedPhone.substring(1, 4)}) ${formattedPhone.substring(4, 7)}-${formattedPhone.substring(7)}`;
                }
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-dialog-actions': DialogActionsBar,
            'loan-notes-component': loanNotesComponent,
            'loader': Loader,
            'k-button': Button,
            'k-dropdown': DropDownList,
            'k-input': Input,
            'k-fade': Fade
        }
    }
</script>

<style>
    @import '/src/assets/css/conditionallyApproveWizard.css';
    @import '/src/assets/css/suspendWizard.css';
</style>